import { USE_MOCK_API } from '../config';
import { newId } from '../lib/ids';
import type {
  AdminSupportTicket,
  AdminSupportTicketThread,
  SupportTicketMessage,
  TicketStatus,
} from '../types/domain';
import { ApiError } from './errors';
import { http } from './http';
import { assertMock, mockDelay } from './mock';

const MOCK_REQUESTER_ID = newId();
const MOCK_TICKET_ID = newId();

function mockThread(): AdminSupportTicketThread {
  const messages: SupportTicketMessage[] = [
    {
      id: newId(),
      ticketId: MOCK_TICKET_ID,
      authorType: 'user',
      authorName: 'Demo Customer',
      authorEmail: 'demo.customer@example.com',
      channel: 'web',
      body: 'The export button completes, but no WAV file appears in Downloads.',
      createdAt: '2026-07-24T10:00:00.000Z',
    },
    {
      id: newId(),
      ticketId: MOCK_TICKET_ID,
      authorType: 'admin',
      authorName: 'Lyrixa Support',
      authorEmail: 'support@lyrixa.ai',
      channel: 'email',
      body: 'Thanks — we are checking the export worker logs and will follow up shortly.',
      createdAt: '2026-07-24T12:30:00.000Z',
    },
  ];

  return {
    id: MOCK_TICKET_ID,
    subject: "Can't download WAV export",
    category: 'Song generation',
    message: messages[0]?.body,
    status: 'In progress',
    date: 'Jul 24',
    createdAt: '2026-07-24T10:00:00.000Z',
    updatedAt: '2026-07-24T12:30:00.000Z',
    requesterId: MOCK_REQUESTER_ID,
    requesterName: 'Demo Customer',
    requesterEmail: 'demo.customer@example.com',
    requesterPurchaseLevel: 0,
    hasUnreadUserMessage: false,
    messages,
  };
}

function mockTickets(): AdminSupportTicket[] {
  const { messages: _messages, ...ticket } = mockThread();
  return [ticket];
}

function ticketPath(ticketId: string): string {
  return `/admin/support/tickets/${encodeURIComponent(ticketId)}`;
}

export const adminSupportApi = {
  async getTickets(): Promise<AdminSupportTicket[]> {
    if (!USE_MOCK_API) {
      return http.get<AdminSupportTicket[]>('/admin/support/tickets');
    }
    assertMock('adminSupportApi.getTickets');
    return mockDelay(mockTickets());
  },

  async getTicketThread(ticketId: string): Promise<AdminSupportTicketThread> {
    if (!USE_MOCK_API) {
      return http.get<AdminSupportTicketThread>(ticketPath(ticketId));
    }
    assertMock('adminSupportApi.getTicketThread');
    const thread = ticketId === MOCK_TICKET_ID ? mockThread() : null;
    if (!thread) throw new ApiError(404, 'Support ticket not found.', 'not_found');
    return mockDelay(thread);
  },

  async updateTicketStatus(ticketId: string, status: TicketStatus): Promise<AdminSupportTicket> {
    if (!USE_MOCK_API) {
      return http.patch<AdminSupportTicket>(ticketPath(ticketId), { status });
    }
    assertMock('adminSupportApi.updateTicketStatus');
    const ticket = mockTickets().find((candidate) => candidate.id === ticketId);
    if (!ticket) throw new ApiError(404, 'Support ticket not found.', 'not_found');
    return mockDelay({ ...ticket, status, updatedAt: new Date().toISOString() });
  },

  async sendReply(
    ticketId: string,
    input: { message: string; closeTicket?: boolean }
  ): Promise<AdminSupportTicketThread> {
    if (!USE_MOCK_API) {
      return http.post<AdminSupportTicketThread>(`${ticketPath(ticketId)}/replies`, input);
    }
    assertMock('adminSupportApi.sendReply');
    const thread = ticketId === MOCK_TICKET_ID ? mockThread() : null;
    if (!thread) throw new ApiError(404, 'Support ticket not found.', 'not_found');
    const createdAt = new Date().toISOString();
    return mockDelay({
      ...thread,
      status: input.closeTicket
        ? 'Resolved'
        : thread.status === 'Open'
          ? 'In progress'
          : thread.status,
      updatedAt: createdAt,
      messages: [
        ...thread.messages,
        {
          id: newId(),
          ticketId,
          authorType: 'admin',
          authorName: 'Lyrixa Support',
          authorEmail: 'support@lyrixa.ai',
          channel: 'email',
          body: input.message.trim(),
          createdAt,
        },
      ],
    });
  },
};
